import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { ChevronRight } from './icons';
import { useMe } from '../../hooks/useMe';
import { setNotificationsApi, type NotificationPrefs } from '../../lib/me';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import walletIcon from '../../assets/figma/ic-empty-wallet.svg';
import arrowUpIcon from '../../assets/figma/ic-arrow-up.svg';
import cardsIcon from '../../assets/figma/ic-cards.svg';
import shieldIcon from '../../assets/figma/profile/shield.svg';

const DEFAULTS: NotificationPrefs = { deposits: true, transfers: true, cards: true, security: true };

const TINT = '#f1f2f4';

const CATEGORIES: { key: keyof NotificationPrefs; icon: string }[] = [
  { key: 'deposits', icon: walletIcon },
  { key: 'transfers', icon: arrowUpIcon },
  { key: 'cards', icon: cardsIcon },
  { key: 'security', icon: shieldIcon },
];

function Toggle({ on }: { on: boolean }) {
  return (
    <span
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
        on ? 'bg-[#34c759]' : 'bg-black/15'
      }`}
    >
      <span
        className={`absolute h-5 w-5 rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

/** Toggle which Telegram-bot notifications the user receives. */
export function NotificationsScreen({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { me, initData, refresh, canFetch } = useMe();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();

  const [prefs, setPrefs] = useState<NotificationPrefs>(me?.notifications ?? DEFAULTS);
  useEffect(() => {
    if (me?.notifications) setPrefs(me.notifications);
  }, [me?.notifications]);

  const toggle = async (key: keyof NotificationPrefs) => {
    if (!canFetch) return;
    const prev = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await setNotificationsApi(initData, { [key]: next[key] });
      refresh();
    } catch {
      setPrefs(prev);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-surface">
      <ScreenBackdrop height={300} fadeStart={24} />
      <div className="relative z-10 flex min-h-screen flex-col px-4 pb-10 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">{t('profile.notifications')}</h1>
        </header>

        <p className="mt-1 mb-4 text-[13px] text-muted">{t('profile.notificationsHint')}</p>

        <div className="flex flex-col gap-2 rounded-[24px] bg-white p-3">
          {CATEGORIES.map((cat, i) => (
            <div key={cat.key}>
              {i > 0 && <div className="mx-auto mb-2 h-px w-[calc(100%-12px)] bg-black/[0.06]" />}
              <button
                onClick={() => toggle(cat.key)}
                className="flex w-full items-center justify-between py-1.5 text-left active:opacity-70"
              >
                <span className="flex items-center gap-3">
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-[12px]"
                    style={{ backgroundColor: TINT }}
                  >
                    <img src={cat.icon} alt="" className="h-5 w-5 brightness-0" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[14px] text-ink">{t(`profile.notif_${cat.key}`)}</span>
                    <span className="text-[12px] text-muted">{t(`profile.notif_${cat.key}_desc`)}</span>
                  </span>
                </span>
                <Toggle on={prefs[cat.key]} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
